import * as React from "react";
import { createPortal } from "react-dom";
import { dissoc } from "ramda";
import styled from "styled-components";

const Inline = styled.div`
  position: relative;
`;

type LayerConxtextType = {
  updateLayer: (id: string, element: React.ReactElement<any>) => void;
  removeLayer: (id: string) => void;
  parentElement: HTMLDivElement;
};

export const LayerContext = React.createContext({} as LayerConxtextType);

type Props = {
  inline?: boolean;
};

type State = {
  layers: Record<string, React.ReactElement<any>>;
  contextPayload: LayerConxtextType;
};

class LayerManager extends React.Component<Props, State> {
  element: HTMLDivElement;

  constructor(props: Props) {
    super(props);

    if (!props.inline) {
      this.element = document.createElement("div");
      this.element.style.position = "absolute";
      this.element.style.top = "0px";
      this.element.style.left = "0px";
      this.element.style.right = "0px";
      document.body.appendChild(this.element);
    }

    this.state = {
      layers: {},
      contextPayload: {
        removeLayer: this.handleRemoveLayer,
        updateLayer: this.handleUpdateLayer,
        parentElement: this.element
      }
    };
  }

  componentDidMount() {
    if (this.props.inline) {
      this.setState({
        contextPayload: {
          ...this.state.contextPayload,
          parentElement: this.element
        }
      });
    }
  }

  componentWillUnmount() {
    if (!this.props.inline) {
      document.body.removeChild(this.element);
    }
  }

  handleUpdateLayer = (id: string, element: React.ReactElement<any>) => {
    this.setState(({ layers }) => ({
      layers: {
        ...layers,
        [id]: element
      }
    }));
  };

  handleRemoveLayer = (id: string) => {
    if (this.state.layers[id]) {
      this.setState(state => ({ layers: dissoc(id, state.layers) }));
    }
  };

  renderLayers() {
    const { layers } = this.state;

    return Object.keys(layers)
      .filter(id => Boolean(layers[id]))
      .map(id => React.cloneElement(layers[id], { key: id }));
  }

  registerInlineElement = (element: HTMLDivElement) => {
    this.element = element;
  };

  render() {
    const { inline, children } = this.props;
    const layers = this.renderLayers();

    return (
      <LayerContext.Provider value={this.state.contextPayload}>
        {!inline && children}
        {inline ? (
          <Inline ref={this.registerInlineElement} className="layer-manager">
            {layers}
            {children}
          </Inline>
        ) : (
          createPortal(layers, this.element)
        )}
      </LayerContext.Provider>
    );
  }
}

export default LayerManager;
